import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type UserRole = 'superadmin' | 'admin' | 'moderator' | 'user' | null;

// Retry logic for network failures
const fetchWithRetry = async (
  fn: () => Promise<any>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<any> => {
  let lastError: any;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Check if it's a network error
      const isNetworkError = 
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('Network request failed') ||
        error?.message?.includes('NetworkError');
      
      if (isNetworkError && i < maxRetries - 1) {
        // Exponential backoff
        const waitTime = delay * Math.pow(2, i);
        
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      
      // If not a network error or last retry, throw
      throw error;
    }
  }
  
  throw lastError;
};

export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        setError(null);
        
        
        const result = await fetchWithRetry(async () => {
          const { data, error } = await supabase.rpc('get_user_role', {
            _user_id: user.id
          });
          
          if (error) throw error;
          return data;
        });

        setRole(result || 'user');
      } catch (err: any) {
        console.error('Error fetching user role:', err);
        setError(err?.message || 'Failed to fetch user role');
        setRole('user'); // Default to user role on error
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isModerator = role === 'moderator' || role === 'admin' || role === 'superadmin';

  return { role, loading, error, isSuperAdmin, isAdmin, isModerator };
}
